// 统一的 APP 图标组件。
// design-v2:icon 直接吃 apps.yaml 下发的 IconSource 对象:
//   - { builtin: key }:命中下面 ICONS 表的 key,渲染预置 SVG
//   - { text: char }:emoji / 单字符文本回退
//   - { url: path }:由后端 /api/v1/apps/icon/<name> 提供的远程图片
// 未匹配 / 加载失败 → 回退到 title 首字母。
import { useEffect, useState } from 'react';
import type { IconSource } from '@blade/os-sdk';
import { DEFAULT_GRADIENT, MarketAppIcon, emojiToTwemojiUrl } from '@blade/os-sdk';
import bladeAgentPng from '../assets/blade-agent.png';
import configWebp from '../assets/config.webp';
import dockerPng from '../assets/docker.png';
import helpDocPng from '../assets/help-doc.png';
import skillsHubPng from '../assets/skills-hub.png';
import { isControlledAppId } from '../license/controlled';
import { useLicenseStore } from '../license/store';

interface AppIconProps {
  icon: IconSource | undefined;
  title: string;
  size?: number;
  appId?: string;
}

export function AppIcon({ icon, title, size = 28, appId }: AppIconProps) {
  const controlled = isControlledAppId(appId);
  const loading = useLicenseStore((state) => state.loading);
  const lastFetchedAt = useLicenseStore((state) => state.lastFetchedAt);
  const enforcedInvalid = useLicenseStore(
    (state) => state.status?.enabled === true && state.validation?.state === 'invalid',
  );
  const loadAll = useLicenseStore((state) => state.loadAll);

  useEffect(() => {
    if (!controlled || loading || lastFetchedAt) return;
    void loadAll();
  }, [controlled, lastFetchedAt, loadAll, loading]);

  const content = renderIconContent(icon, title, size);
  if (!controlled) return content;

  return (
    <span
      aria-hidden
      style={{
        position: 'relative',
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {content}
      {enforcedInvalid ? <LockBadge size={size} /> : null}
    </span>
  );
}

function renderIconContent(icon: IconSource | undefined, title: string, size: number) {
  if (icon && 'type' in icon && icon.type === 'tabler') {
    return <MarketAppIcon icon={icon} size={size} />;
  }
  if (icon && 'url' in icon) {
    return <RemoteIcon src={icon.url} title={title} size={size} />;
  }
  if (icon && 'builtin' in icon) {
    const Svg = ICONS[icon.builtin];
    if (Svg) return <Svg size={size} />;
  }
  if (icon && 'text' in icon) {
    const bg = icon.background ?? DEFAULT_GRADIENT;
    return <TwemojiIcon emoji={icon.text} background={bg} size={size} />;
  }
  const letter = icon && 'builtin' in icon
    ? icon.builtin.slice(0, 1).toUpperCase()
    : title.slice(0, 1).toUpperCase();
  return (
    <span
      aria-hidden
      style={{
        fontSize: size * 0.72,
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {letter}
    </span>
  );
}

function TwemojiIcon({ emoji, background, size }: { emoji: string; background: string; size: number }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [emoji]);
  const imgSize = Math.round(size * 0.56);
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.25,
        background,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
    >
      {failed ? (
        <span style={{ fontSize: size * 0.5, lineHeight: 1 }}>{emoji}</span>
      ) : (
        <img
          src={emojiToTwemojiUrl(emoji)}
          alt=""
          aria-hidden
          width={imgSize}
          height={imgSize}
          onError={() => setFailed(true)}
          style={{ display: 'block' }}
        />
      )}
    </span>
  );
}

function LockBadge({ size }: { size: number }) {
  const fontSize = Math.max(8, Math.round(size * 0.2));
  const height = fontSize + 4;
  return (
    <span
      data-testid="license-lock-badge"
      title="证书已过期，服务已停用"
      style={{
        position: 'absolute',
        right: -3,
        bottom: -3,
        height,
        lineHeight: `${height}px`,
        padding: '0 3px',
        borderRadius: 3,
        background: '#dc2626',
        color: '#ffffff',
        border: '1px solid #ffffff',
        fontSize,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.3)',
      }}
    >
      已过期
    </span>
  );
}

function RemoteIcon({
  src,
  title,
  size,
}: {
  src: string;
  title: string;
  size: number;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    // 坏链回退到首字母，避免破坏桌面布局。
    return (
      <span
        aria-hidden
        style={{
          fontSize: size * 0.72,
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {title.slice(0, 1).toUpperCase()}
      </span>
    );
  }
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: size * 0.26,
        overflow: 'hidden',
      }}
    >
      <img
        src={src}
        alt=""
        aria-hidden
        onError={() => setFailed(true)}
        style={{
          width: size,
          height: size,
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </span>
  );
}

type IconComponent = (p: { size: number }) => JSX.Element;

const ICONS: Record<string, IconComponent> = {
  dashboard: DashboardIcon,
  containers: ContainersIcon,
  terminal: TerminalIcon,
  profile: ProfileIcon,
  agent: AgentIcon,
  hello: HelloIcon,
  'skills-hub': SkillsHubIcon,
  'help-doc': HelpDocIcon,
  config: ConfigIcon,
  'file-manager': FileManagerIcon,
  'file-preview': FilePreviewIcon,
  'llm-gateway': LlmGatewayIcon,
  market: MarketIcon,
  'service-manager': ServiceManagerIcon,
  'image-manager': ImageManagerIcon,
};

// ---------------- SVG 定义：拟 1Panel 风格 ----------------
// 统一的 24×24 viewBox + 圆角背景 + 主色前景。

function Tile({
  children,
  bg = 'linear-gradient(135deg, #4c8dff 0%, #1453cc 100%)',
  size,
}: {
  children: React.ReactNode;
  bg?: string;
  size: number;
}) {
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.26,
        background: bg,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 10px rgba(15, 23, 42, 0.18)',
      }}
    >
      {children}
    </span>
  );
}

function DashboardIcon({ size }: { size: number }) {
  const inner = size * 0.62;
  return (
    <Tile size={size}>
      <svg
        width={inner}
        height={inner}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="3" y="4" width="18" height="6" rx="2" fill="#ffffff" />
        <rect x="3" y="13" width="11" height="7" rx="2" fill="#ffffff" opacity="0.85" />
        <rect x="16" y="13" width="5" height="7" rx="2" fill="#ffffff" opacity="0.6" />
      </svg>
    </Tile>
  );
}

function ContainersIcon({ size }: { size: number }) {
  return (
    <img
      src={dockerPng}
      alt=""
      aria-hidden
      style={{
        width: size,
        height: size,
        objectFit: 'cover',
        display: 'block',
        borderRadius: size * 0.26,
      }}
    />
  );
}

function SkillsHubIcon({ size }: { size: number }) {
  return (
    <img
      src={skillsHubPng}
      alt=""
      aria-hidden
      style={{
        width: size,
        height: size,
        objectFit: 'cover',
        display: 'block',
        borderRadius: size * 0.26,
      }}
    />
  );
}

function HelpDocIcon({ size }: { size: number }) {
  return (
    <img
      src={helpDocPng}
      alt=""
      aria-hidden
      style={{
        width: size,
        height: size,
        objectFit: 'cover',
        display: 'block',
        borderRadius: size * 0.26,
      }}
    />
  );
}

function ConfigIcon({ size }: { size: number }) {
  // 原图带一圈白色留白；用容器裁切把图本身放大 1.2 倍并居中，外框按正常
  // 圆角展示，视觉上就看不到白边了。
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: size * 0.26,
        overflow: 'hidden',
      }}
    >
      <img
        src={configWebp}
        alt=""
        aria-hidden
        style={{
          width: size * 1.2,
          height: size * 1.2,
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </span>
  );
}

function TerminalIcon({ size }: { size: number }) {
  const inner = size * 0.6;
  return (
    <Tile size={size} bg="linear-gradient(135deg, #12182a 0%, #1f2533 100%)">
      <svg
        width={inner}
        height={inner}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="2.5"
          y="4.5"
          width="19"
          height="15"
          rx="2.5"
          stroke="#4ade80"
          strokeWidth="2"
        />
        <path
          d="M6 9l3.2 3L6 15"
          stroke="#4ade80"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11.5 15h6.5"
          stroke="#4ade80"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </Tile>
  );
}

function ProfileIcon({ size }: { size: number }) {
  const inner = size * 0.62;
  return (
    <Tile size={size} bg="linear-gradient(135deg, #7aa2ff 0%, #4c8dff 100%)">
      <svg
        width={inner}
        height={inner}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="9" r="4" fill="#ffffff" />
        <path
          d="M4 20c1.2-4 4.4-6 8-6s6.8 2 8 6"
          stroke="#ffffff"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </Tile>
  );
}

function AgentIcon({ size }: { size: number }) {
  return (
    <img
      src={bladeAgentPng}
      alt=""
      aria-hidden
      style={{
        width: size,
        height: size,
        objectFit: 'cover',
        display: 'block',
        borderRadius: size * 0.26,
      }}
    />
  );
}

function FileManagerIcon({ size }: { size: number }) {
  const inner = size * 0.62;
  return (
    <Tile size={size} bg="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)">
      <svg
        width={inner}
        height={inner}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2 6a2 2 0 012-2h5l2 2h9a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
          fill="#ffffff"
        />
        <path
          d="M2 9h20v9a2 2 0 01-2 2H4a2 2 0 01-2-2V9z"
          fill="#ffffff"
          opacity="0.85"
        />
      </svg>
    </Tile>
  );
}

function FilePreviewIcon({ size }: { size: number }) {
  const inner = size * 0.62;
  return (
    <Tile size={size} bg="linear-gradient(135deg, #6366f1 0%, #4338ca 100%)">
      <svg width={inner} height={inner} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 2h8l6 6v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z" fill="#ffffff" />
        <path d="M14 2l6 6h-4a2 2 0 01-2-2V2z" fill="#ffffff" opacity="0.6" />
        <circle cx="12" cy="15" r="3.5" stroke="#6366f1" strokeWidth="1.5" fill="none" />
        <circle cx="12" cy="15" r="1.5" fill="#6366f1" />
      </svg>
    </Tile>
  );
}

function LlmGatewayIcon({ size }: { size: number }) {
  // 模型服务网关：中心枢纽连多个节点，表达「统一入口分发到各模型服务」。
  const inner = size * 0.64;
  return (
    <Tile size={size} bg="linear-gradient(135deg, #a855f7 0%, #6d28d9 100%)">
      <svg
        width={inner}
        height={inner}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 12L5 5M12 12L5 19M12 12h7"
          stroke="#ffffff"
          strokeWidth="1.8"
          strokeLinecap="round"
          opacity="0.7"
        />
        <circle cx="12" cy="12" r="3.4" fill="#ffffff" />
        <circle cx="4.5" cy="4.5" r="2.2" fill="#ffffff" />
        <circle cx="4.5" cy="19.5" r="2.2" fill="#ffffff" />
        <circle cx="20" cy="12" r="2.2" fill="#ffffff" />
      </svg>
    </Tile>
  );
}

function MarketIcon({ size }: { size: number }) {
  // 应用市场:购物袋 + 里面一个应用方块,表达「逛市场、装应用」。
  const inner = size * 0.6;
  return (
    <Tile size={size} bg="linear-gradient(135deg, #f472b6 0%, #7c3aed 100%)">
      <svg
        width={inner}
        height={inner}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M6 8h12l-1 12a2 2 0 01-2 2H9a2 2 0 01-2-2L6 8z" fill="#ffffff" />
        <path d="M8.5 8V6a3.5 3.5 0 017 0v2" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" />
        <rect x="10" y="11.5" width="4" height="4" rx="1" fill="#7c3aed" />
      </svg>
    </Tile>
  );
}

function ServiceManagerIcon({ size }: { size: number }) {
  // 服务管理:两层「服务机架」+ 右下角运行状态点,表达「托管运行中的服务」。
  const inner = size * 0.62;
  return (
    <Tile size={size} bg="linear-gradient(135deg, #0ea5e9 0%, #1453cc 100%)">
      <svg
        width={inner}
        height={inner}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="3" y="4" width="18" height="7" rx="2" fill="#ffffff" />
        <rect x="3" y="13" width="18" height="7" rx="2" fill="#ffffff" opacity="0.85" />
        <circle cx="7" cy="7.5" r="1.2" fill="#1453cc" />
        <circle cx="7" cy="16.5" r="1.2" fill="#1453cc" />
        <circle cx="18.5" cy="18" r="3" fill="#22c55e" stroke="#ffffff" strokeWidth="1.4" />
      </svg>
    </Tile>
  );
}

function ImageManagerIcon({ size }: { size: number }) {
  // 镜像管理:叠放的「镜像盘片」+ 向下箭头,表达「导入/管理镜像」。
  const inner = size * 0.62;
  return (
    <Tile size={size} bg="linear-gradient(135deg, #6366f1 0%, #1453cc 100%)">
      <svg
        width={inner}
        height={inner}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <ellipse cx="12" cy="6" rx="7" ry="2.6" fill="#ffffff" />
        <path d="M5 6v5c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6V6" stroke="#ffffff" strokeWidth="1.6" fill="none" />
        <path d="M5 11v5c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6v-5" stroke="#ffffff" strokeWidth="1.6" fill="none" opacity="0.85" />
        <circle cx="18.5" cy="18" r="3.2" fill="#22c55e" stroke="#ffffff" strokeWidth="1.3" />
        <path d="M18.5 16.4v3.2M17.2 18.3l1.3 1.3 1.3-1.3" stroke="#ffffff" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </Tile>
  );
}

function HelloIcon({ size }: { size: number }) {
  const inner = size * 0.62;
  return (
    <Tile size={size} bg="linear-gradient(135deg, #fbbf24 0%, #f97316 100%)">
      <svg
        width={inner}
        height={inner}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7 11V6.5a1.5 1.5 0 113 0V10"
          stroke="#ffffff"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M10 10V5.5a1.5 1.5 0 113 0V10"
          stroke="#ffffff"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M13 10V6a1.5 1.5 0 113 0v5"
          stroke="#ffffff"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M16 8.5a1.5 1.5 0 113 0V14c0 3.3-2.7 6-6 6h-1a5 5 0 01-5-5c0-1 .4-1.8 1-2.5"
          stroke="#ffffff"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </Tile>
  );
}
